import React, { useState } from 'react'
import { PanelButton } from '@components/Panel/PanelButton'
import { useUSDCMetadata } from '@components/Web3/UserOpTxModal/usdc/qr-fund/useUSDCAddress'
import { baseScanUrl } from '@components/Web3/utils'
import { Box, Icon, Text, TextButton } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useBalance } from 'hooks/useBalance'
import useCopyToClipboard from 'hooks/useCopyToClipboard'
import { useEnvironment } from 'hooks/useEnvironment'
import { useIsTradingEnabled } from 'hooks/useTradingEnabled'
import { useUSDCBalance } from 'hooks/useUsdcBalance'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { useStore } from 'store/store'

import { DetectUndeployedSimpleAccount } from '../DetectUndeployedSimpleAccount/DetectUndeployedSimpleAccount'

import { trackClickedAddFunds } from './fundWalletAnalytics'

/**
 * Omega uses TradingWallet, testnets use this
 */
export function TestnetTownsWalletDisplay() {
    const { data: aaAddress } = useMyAbstractAccountAddress()
    const { baseChain } = useEnvironment()
    const setFundWalletModalType = useStore((state) => state.setFundWalletModalType)
    const balance = useBalance({
        address: aaAddress,
    })
    const { data: usdcBalance } = useUSDCBalance({
        address: aaAddress,
        enabled: !!aaAddress,
    })
    const { address: usdcAddress } = useUSDCMetadata()
    const { openPanel } = usePanelActions()
    const [copied, setCopied] = useState(false)
    const [, copy] = useCopyToClipboard()

    const handleDeposit = () => {
        trackClickedAddFunds({ entrypoint: 'profile' })
        setFundWalletModalType('eth')
    }

    const handleReceiveUSDC = () => {
        setFundWalletModalType('usdc-crypto')
    }

    const handleSend = () => {
        openPanel('transfer-assets', {
            assetSource: aaAddress,
            data: JSON.stringify({
                assetAddressOrIdentifier: 'BASE_ETH',
            }),
        })
    }

    const handleSendUSDC = () => {
        openPanel('transfer-assets', {
            assetSource: aaAddress,
            data: JSON.stringify({
                assetAddressOrIdentifier: usdcAddress,
            }),
        })
    }

    const handleViewAssets = () => {
        openPanel('wallet', { assetSource: aaAddress })
    }

    const handleCopy = async (e: React.MouseEvent) => {
        e.preventDefault()
        if (aaAddress) {
            const success = await copy(aaAddress)
            setCopied(success)
            if (success) {
                setTimeout(() => setCopied(false), 1000)
            }
        }
    }

    // if trading wallet is enabled, we can hide the legacy wallet and vice versa
    const isTradingEnabled = useIsTradingEnabled().isTradingEnabled

    if (isTradingEnabled) {
        return null
    }

    return (
        <>
            <DetectUndeployedSimpleAccount />
            <Box padding rounded="sm" background="level2" gap="md">
                <Box gap="md">
                    <Box horizontal justifyContent="spaceBetween" alignItems="center">
                        <Box horizontal alignItems="center" gap="sm">
                            <Text strong color="default">
                                Towns Wallet
                            </Text>
                            <Box
                                as="a"
                                href={`${baseScanUrl(baseChain.id)}/address/${aaAddress}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: 'flex', cursor: 'pointer' }}
                                opacity={{ hover: '0.8' }}
                            >
                                <Icon type="etherscan" size="square_sm" color="gray2" />
                            </Box>
                        </Box>
                        <TextButton onClick={handleViewAssets}>View Assets</TextButton>
                    </Box>
                    <Box centerContent gap="sm" paddingY="sm">
                        <Box horizontal alignItems="center" gap="sm">
                            <Icon type="baseEth" size="square_lg" />
                            <Text strong size="h3">
                                {balance.data?.formatted ?? 0} ETH
                            </Text>
                        </Box>
                        <Box horizontal alignItems="center" gap="sm">
                            <Icon type="usdc" size="square_lg" />
                            <Text strong size="h3">
                                {usdcBalance?.formatted ?? '0'} USDC
                            </Text>
                        </Box>
                    </Box>
                    <Box horizontal justifyContent="spaceBetween" display="flex" gap="sm">
                        <PanelButton
                            grow
                            centerContent
                            background="level3"
                            padding="md"
                            height="x10"
                            width="x12"
                            onClick={handleDeposit}
                        >
                            <Box direction="column" alignItems="center" gap="sm">
                                <Icon type="plus" size="square_sm" color="cta1" />
                                <Text
                                    size="md"
                                    color="greenBlue"
                                    paddingBottom="xs"
                                    paddingTop="xxs"
                                >
                                    Deposit
                                </Text>
                            </Box>
                        </PanelButton>
                        <PanelButton
                            grow
                            centerContent
                            background="level3"
                            padding="md"
                            height="x10"
                            width="x12"
                            onClick={handleSend}
                        >
                            <Box direction="column" alignItems="center" gap="xs">
                                <Icon type="arrowRightUp" size="square_md" color="cta1" />
                                <Text
                                    size="md"
                                    color="greenBlue"
                                    paddingBottom="xs"
                                    paddingTop="xxs"
                                >
                                    Send
                                </Text>
                            </Box>
                        </PanelButton>
                        <PanelButton
                            grow
                            centerContent
                            background="level3"
                            padding="md"
                            height="x10"
                            width="x12"
                            onClick={handleCopy}
                        >
                            <Box direction="column" alignItems="center" gap="sm">
                                {!copied ? (
                                    <Icon type="copy" size="square_sm" color="cta1" />
                                ) : (
                                    <Icon type="check" size="square_sm" color="cta1" />
                                )}
                                <Text
                                    size="md"
                                    color="greenBlue"
                                    paddingBottom="xs"
                                    paddingTop="xxs"
                                >
                                    {copied ? 'Copied!' : 'Copy'}
                                </Text>
                            </Box>
                        </PanelButton>
                    </Box>
                    {/* USDC Actions */}
                    <Box horizontal justifyContent="spaceBetween" display="flex" gap="sm">
                        <PanelButton
                            grow
                            centerContent
                            background="level3"
                            padding="md"
                            height="x10"
                            width="x12"
                            onClick={handleReceiveUSDC}
                        >
                            <Box direction="column" alignItems="center" gap="sm">
                                <Icon type="qrCode" size="square_sm" color="cta1" />
                                <Text
                                    size="md"
                                    color="greenBlue"
                                    paddingBottom="xs"
                                    paddingTop="xxs"
                                >
                                    Receive USDC
                                </Text>
                            </Box>
                        </PanelButton>
                        <PanelButton
                            grow
                            centerContent
                            background="level3"
                            padding="md"
                            height="x10"
                            width="x12"
                            onClick={handleSendUSDC}
                        >
                            <Box direction="column" alignItems="center" gap="xs">
                                <Icon type="arrowRightUp" size="square_md" color="cta1" />
                                <Text
                                    size="md"
                                    color="greenBlue"
                                    paddingBottom="xs"
                                    paddingTop="xxs"
                                >
                                    Send USDC
                                </Text>
                            </Box>
                        </PanelButton>
                    </Box>
                </Box>
            </Box>
        </>
    )
}
